import { useFormikContext } from 'formik';
import ms from 'ms';
import { func } from 'prop-types';
import { useCallback, useMemo, useRef } from 'react';
import { useQuery } from 'react-query';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';

import { useFiltersContext } from 'hb-react/frontoffice/apps/Filters/contexts/FiltersContext';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import { useAmplitude } from 'hb-react/shared/hooks/useAmplitude';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

import ButtonCancel from './ButtonCancel';
import ButtonShowTotalPreFilteredItems from './ButtonShowTotalPreFilteredItems';
import FiltersBlock from './FiltersBlock';

const Modal = ({ onClose }) => {
  const { config } = useFiltersContext();
  const { values, setValues } = useFormikContext();
  const onOpenValues = useRef(values);

  const { trackEvent } = useAmplitude();

  const { isLoading, data: { overlay: filters = [] } = {} } = useQuery(
    ['filters', config.filters_path],
    () => axios.get(config.filters_path).then(({ data }) => data),
    {
      staleTime: ms('10m'),
    },
  );

  const handleOnCancelModal = useCallback(() => {
    setValues(onOpenValues.current, false);
    trackEvent('PeopleDirectory|User cancels filter modal');
    onClose();
  }, [onClose, setValues, trackEvent]);

  // Memoize the modal to prevent unnecessary re-renders
  return useMemo(
    () => (
      <ModalWithTemplate
        footer={
          <Box>
            <ButtonCancel handleCancelModal={handleOnCancelModal} />
            <Box ml={4}>
              <ButtonShowTotalPreFilteredItems onClose={onClose} />
            </Box>
          </Box>
        }
        header={config.modalTitle}
        onCloseModal={handleOnCancelModal}
        pb={{ _: 15, l: 3 }}
        screenReadersCloseButtonText={s__(
          'Accessibility|Close more filters window',
        )}
      >
        <Box display="block" minWidth="100%" data-testid="filters-block">
          {isLoading ? (
            <Loader size={24} />
          ) : (
            <FiltersBlock filters={filters} />
          )}
        </Box>
      </ModalWithTemplate>
    ),
    [handleOnCancelModal, config.modalTitle, isLoading, filters, onClose],
  );
};

Modal.propTypes = {
  onClose: func.isRequired,
};

export default Modal;
